import { computed, inject, Injectable, signal } from '@angular/core';
import {
  RouteDirectionsReq,
  RouteDirectionsRes,
  RouteSegmentPoint,
  RouteWaypoint,
} from '../interfaces/route-planning-api';
import { Observable } from 'rxjs';
import { MapLayer } from '../interfaces/map';
import { AddStopElClass } from './map-template.service';
import { RestApiService } from '@core/services/rest-api.service';
import * as L from 'leaflet';
import MapZoomRulesUtils from '../utils/map-zoom-rules.utils';
import MobileUtils from '@shared/utils/mobile.utils';

@Injectable({ providedIn: 'root' })
export class RoutePlannerService {
  private api = inject(RestApiService);

  readonly waypoints = signal<RouteWaypoint[]>([]);
  readonly canFetchRoute = computed(() => this.waypoints().length >= 2);
  readonly hasEnoughWaypoints = computed(() => this.waypoints().length >= 2);
  readonly hasWaypoints = computed(() => !!this.waypoints().length);

  private waypointMarkers: MapLayer[] = [];
  private routePlannerPolylines: MapLayer[] = [];
  private lastDraggedWaypointIndex: number | undefined = undefined;

  addWaypoint(waypoint: RouteWaypoint): void {
    this.waypoints.update((wps) => [...wps, waypoint]);
  }

  getDirections(zoom: number): Observable<RouteDirectionsRes> {
    const payload: RouteDirectionsReq = {
      waypoints: this.waypoints(),
      maxBins: MapZoomRulesUtils.getMaxBins(zoom),
      waypointSearchRadius: MapZoomRulesUtils.getSearchRadius(zoom),
    };
    return this.api.post<RouteDirectionsRes>('route/directions', payload);
  }

  drawRoute(route: RouteDirectionsRes, map: L.Map): void {
    this.clearRoutePolylines(map);

    const latLngs = route.Segments.map((s) => L.latLng(s.Latitude, s.Longitude));
    const routeLine: MapLayer = L.polyline(latLngs, {
      color: '#1a73e8',
      weight: 8,
    });
    routeLine.extraData = { origin: 'user', isRoutePlannerSegment: true };
    routeLine.addTo(map);
    this.routePlannerPolylines.push(routeLine);

    this.snapMarkersToRoute(route.Segments);
  }

  attachAddStopEventHandlerToPolylinePopups(layers: MapLayer[], map: L.Map): void {
    layers.forEach((l) => {
      let clickedLatLng: L.LatLng | null = null;

      (l as L.Polyline).on('click', (e: L.LeafletMouseEvent) => {
        clickedLatLng = e.latlng;
      });

      l.on('popupopen', () => {
        const btn = l.getPopup()?.getElement()?.querySelector(`.${AddStopElClass}`);
        btn?.addEventListener('click', () => {
          const latLng = clickedLatLng ?? ((l as L.Polyline).getLatLngs()[0] as L.LatLng);
          const waypoint: RouteWaypoint = { latitude: latLng.lat, longitude: latLng.lng };

          this.addWaypoint(waypoint);
          this.addWaypointMarker(waypoint, map);

          map.closePopup();
        });
      });
    });
  }

  removeWaypointAndRearrange(marker: MapLayer, map: L.Map): void {
    const index = marker.extraData!.waypointIndex!;
    this.lastDraggedWaypointIndex = index;

    map.removeLayer(marker);
    this.waypointMarkers = this.waypointMarkers.filter((m) => m !== marker);
    this.waypoints.update((wps) => wps.filter((_, i) => i !== index));

    // if less than 2 waypoints left, clear route polyline but keep remaining marker
    if (this.waypoints().length < 2) {
      this.clearRoutePolylines(map);

      // rearrange the single remaining marker back to A
      if (this.waypointMarkers.length === 1) {
        (this.waypointMarkers[0] as L.Marker).setIcon(this.buildWaypointIcon('A'));
        this.waypointMarkers[0].extraData = {
          ...this.waypointMarkers[0].extraData,
          waypointIndex: 0,
        };
      }
      return;
    }

    // rearrange remaining markers
    this.waypointMarkers.forEach((m, i) => {
      (m as L.Marker).setIcon(this.buildWaypointIcon(String.fromCharCode(65 + i)));
      m.extraData = { ...m.extraData, waypointIndex: i };
    });

    // re-fetch route with remaining waypoints
    this.getDirections(map.getZoom()).subscribe((route) => this.drawRoute(route, map));
  }

  clearWaypoints(map: L.Map): void {
    this.waypointMarkers.forEach((m) => map.removeLayer(m));
    this.waypointMarkers = [];
    this.waypoints.set([]);
    this.clearRoutePolylines(map);
    this.lastDraggedWaypointIndex = undefined;
  }

  private snapMarkersToRoute(segments: RouteSegmentPoint[]): void {
    if (!this.lastDraggedWaypointIndex) return;

    const marker = this.waypointMarkers.find(
      (m) => m.extraData?.waypointIndex === this.lastDraggedWaypointIndex,
    );

    if (!marker) return;

    const markerLatLng = (marker as L.Marker).getLatLng();

    const closest = segments.reduce((prev, seg) => {
      const segLatLng = L.latLng(seg.Latitude, seg.Longitude);
      const prevLatLng = L.latLng(prev.Latitude, prev.Longitude);
      return markerLatLng.distanceTo(segLatLng) < markerLatLng.distanceTo(prevLatLng) ? seg : prev;
    });

    (marker as L.Marker).setLatLng(L.latLng(closest.Latitude, closest.Longitude));

    this.lastDraggedWaypointIndex = undefined; // reset after snapping
  }

  private addWaypointMarker(waypoint: RouteWaypoint, map: L.Map): void {
    const index = this.waypoints().length - 1;

    const marker: MapLayer = L.marker([waypoint.latitude, waypoint.longitude], {
      // disable dragging on mobile
      draggable: MobileUtils.isMobile() ? false : true,
      icon: this.buildWaypointIcon(String.fromCharCode(65 + index)),
    });

    marker.extraData = {
      origin: 'user',
      isWaypoint: true,
      waypointIndex: index,
    };

    marker.on('click', () => this.removeWaypointAndRearrange(marker, map));
    this.attachWaypointDragHandlerIfNeeded(marker);

    marker.addTo(map);

    this.waypointMarkers.push(marker);
  }

  /** marker dragging is disabled in mobile mode */
  private attachWaypointDragHandlerIfNeeded(marker: MapLayer): void {
    if (MobileUtils.isMobile()) return;

    marker.on('dragend', () => {
      const latLng = (marker as L.Marker).getLatLng();
      const idx = marker.extraData!.waypointIndex!;
      this.lastDraggedWaypointIndex = idx;

      // update waypoints signal to trigger API call
      this.waypoints.update((wps) =>
        wps.map((wp, i) => (i === idx ? { latitude: latLng.lat, longitude: latLng.lng } : wp)),
      );
    });
  }

  private clearRoutePolylines(map: L.Map): void {
    this.routePlannerPolylines.forEach((p) => map.removeLayer(p));
    this.routePlannerPolylines = [];
  }

  private buildWaypointIcon(label: string): L.DivIcon {
    return L.divIcon({
      html: `
      <svg xmlns="http://www.w3.org/2000/svg" width="28" height="36" viewBox="-2 -2 32 40">
        <path d="M14 0 C6.268 0 0 6.268 0 14 C0 24.5 14 36 14 36 C14 36 28 24.5 28 14 C28 6.268 21.732 0 14 0 Z"
            fill="#1a73e8" stroke="black" stroke-width="3"/>
        <text x="14" y="18" text-anchor="middle" dominant-baseline="middle"
            fill="white" font-size="16" font-weight="700" font-family="sans-serif">
          ${label}
        </text>
      </svg>
    `,
      className: 'waypoint-marker',
      iconSize: [28, 36],
      iconAnchor: [14, 36],
      popupAnchor: [0, -36],
    });
  }
}
